import { useLingui } from "@lingui/react/macro";
import { useMemo } from "react";
import NoResults from "@/components/NoResults/NoResults";
import type { ShiftsWithContents } from "@/api/Shifts";
import { List } from "./List/List";
import { Timeline } from "./Timeline/Timeline";
import { viewModeSchema } from "./usePlanningViewToolbar";
import {
  applyCompetenceFilter,
  useCompetenceFilter,
} from "./useCompetenceFilter";
import { applyTeamFilter, useTeamFilter } from "./useTeamFilter";
import { hideFullyCoveredDeletedShifts } from "./planningViewUtils";
import type { z } from "zod";

type ShiftsPanelContentProps = {
  shiftsWithContents: ShiftsWithContents;
  viewMode: z.infer<typeof viewModeSchema>;
};

export const ShiftsPanelContent = ({
  shiftsWithContents,
  viewMode,
}: ShiftsPanelContentProps) => {
  const { t } = useLingui();
  const { selected: selectedCompetences, allSelected: allCompetencesSelected } =
    useCompetenceFilter();
  const { selected: selectedTeams, allSelected: allTeamsSelected } =
    useTeamFilter();

  const filtered = useMemo(
    () =>
      hideFullyCoveredDeletedShifts(
        applyTeamFilter(
          applyCompetenceFilter(
            shiftsWithContents,
            selectedCompetences,
            allCompetencesSelected,
          ),
          selectedTeams,
          allTeamsSelected,
        ),
      ),
    [
      shiftsWithContents,
      selectedCompetences,
      allCompetencesSelected,
      selectedTeams,
      allTeamsSelected,
    ],
  );

  const isEmpty =
    filtered.before.length === 0 &&
    filtered.relevant.length === 0 &&
    filtered.after.length === 0;

  if (isEmpty) {
    return (
      <NoResults
        message={t`Inga arbetspass schemalagda för dagen.`}
        padding={24}
      />
    );
  }

  if (viewMode === viewModeSchema.enum.list) {
    return <List shiftsWithContents={filtered} />;
  }

  return <Timeline shiftsWithContents={filtered} />;
};
